import React, { useEffect, useState } from "react";
import { Box, Avatar, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { deleteAppointment, listAppointments } from '../redux/actions/appointmentActions';
import './timeline.css'; // Import the CSS file

import { useParams } from "react-router-dom";
const AgendaFileCard = ({ appointments }) => {
  const { t } = useTranslation(['common', 'buttons']);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();
  const { user } = useSelector((state) => state.userLoaded);
  const [hoveredAppointmentId, setHoveredAppointmentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  useEffect(() => {
    console.log(appointments);
  }, [appointments]);

  const handleNavigate = (id1) => {
    navigate(`/appointment/${id1}`, { state: { caseId: id } });
  };

  const handleDeleteClick = (appointmentId, e) => {
    e.stopPropagation();
    setAppointmentToDelete(appointmentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (appointmentToDelete) {
      try {
        await dispatch(deleteAppointment(appointmentToDelete));
        await dispatch(listAppointments());
      } catch (error) {
        console.error('Error deleting appointment:', error);
      }
    }
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
  };

  // Helper function to format date as day-month-year
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const sortedAppointments = appointments.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const lastSession = sortedAppointments.filter(app => app.calendarType === 'Hearing' && new Date(app.startDate) < new Date()).pop();
  const nextSession = sortedAppointments.filter(app => app.calendarType === 'Hearing' && new Date(app.startDate) >= new Date())[0];
  const lastAppointment = sortedAppointments.filter(app => app.calendarType === 'Appointment' && new Date(app.startDate) < new Date()).pop();
  const nextAppointment = sortedAppointments.filter(app => app.calendarType === 'Appointment' && new Date(app.startDate) >= new Date())[0];

  const displayAppointments = [
    { label: t('agendaCard.lastSession'), appointment: lastSession },
    { label: t('agendaCard.nextSession'), appointment: nextSession },
    { label: t('agendaCard.lastAppointment'), appointment: lastAppointment },
    { label: t('agendaCard.nextAppointment'), appointment: nextAppointment }
  ].filter(item => item.appointment).sort((a, b) => new Date(a.appointment.startDate) - new Date(b.appointment.startDate));

  return (
    <Box
      className="list-items"
      sx={{
        marginTop: 2,
        overflowY: "auto",
        maxHeight: "450px",
        "&::-webkit-scrollbar": {
          width: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "lightgrey",
          borderRadius: "0",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#888",
        },
      }}
    >
      <div className="custom-timeline">
        {displayAppointments.map(({ label, appointment }) => (
          <div className="timeline-item" key={appointment._id}>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <Typography variant="subtitle2" 
              sx={{
                color: "rgb(117, 117, 117)",
                display: "flex",
                flexDirection: "row",
                fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "9px",
                fontWeight: 500,
                height: "12px",
                letterSpacing: "0.75px",
                lineHeight: "12px",
                marginBottom: "4px",
                textTransform: "uppercase",
              }}>
                {label}
              </Typography>
              <Box
                onMouseEnter={() => setHoveredAppointmentId(appointment._id)}
                onMouseLeave={() => setHoveredAppointmentId(null)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "top",
                  borderTop: "1px solid lightgrey",
                  padding: "5px",
                  paddingTop: "5px",
                  marginBottom: "5px",
                  "&:hover": {
                    backgroundColor: "#ecf5fb",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "top",
                    justifyContent: "top",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <Box
                    sx={{
                      color: "rgb(53, 53, 53)",
                      display: "flex",
                      flexDirection: "row",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "10px",
                      height: "15.75px",
                      lineHeight: "21px",
                      whiteSpace: "nowrap",
                      // overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "27ch",
                    }}
                  >
                    {formatDate(appointment?.startDate)}
                    {", "}
                    {new Date(appointment?.startDate).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    -{" "}
                    {formatDate(appointment?.endDate) ===
                    formatDate(appointment?.startDate)
                      ? new Date(appointment?.endDate).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : `${formatDate(
                          appointment?.endDate
                        )} ${new Date(
                          appointment?.endDate
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}`}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: appointment.colour,
                        marginRight: "5px",
                      }}
                    />
                    <Box
                      sx={{
                        color: "rgb(0, 91, 146)",
                        cursor: "pointer",
                        display: "block",
                        fontFamily:
                          '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                        fontSize: "11.5px",
                        fontWeight: 400,
                        height: "13.5px",
                        lineHeight: "13.5px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "23ch",
                        paddingBottom: "2px",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => handleNavigate(appointment._id)}
                    >
                      {appointment?.subject}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    margin: 0,
                    marginTop: "2px",
                  }}
                >
                  <Box
                    sx={{
                      color: "rgb(53, 53, 53)",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "11.5px",
                      fontWeight: 400,
                      height: "15.75px",
                      lineHeight: "15.75px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {appointment?.owner?.firstName +
                      " " +
                      appointment?.owner?.lastName}
                    <Avatar
                      alt={user?.firstName}
                      src={
                        user?.avatar ? user?.avatar : "/static/images/avatar/1.jpg"
                      }
                      sx={{
                        fontFamily:
                          '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                        fontSize: "9px",
                        height: "18px",
                        lineHeight: "18px",
                        width: "18px",
                        marginLeft: "8px",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      color: "rgb(53, 53, 53)",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "10.256px",
                      fontWeight: 500,
                      height: "12.375px",
                      lineHeight: "12.384px",
                      marginTop: "2px",
                    }}
                  >
                    {appointment?.type}
                  </Box>
                </Box>
                {hoveredAppointmentId === appointment._id && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(appointment._id, e)}
                    sx={{
                      color: '#666',
                      '&:hover': {
                        color: '#d32f2f',
                      },
                      marginLeft: '8px',
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('common:confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common:confirm_delete_appointment')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:secondary.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('buttons:danger.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaFileCard;